import { useState, useEffect } from 'react';
import { bitable } from '@lark-base-open/js-sdk';
import { Button, Toast, Typography, Spin, Select, Switch, Progress, Banner, Divider } from '@douyinfe/semi-ui';
import { IconUpload, IconFile } from '@douyinfe/semi-icons';
import { useBaseData, AttachmentInfo } from './hooks/useBaseData';
import { uploadToGoogleDrive } from './services/api';
import { fetchProgress } from './services/progress';
import { directUploadFile, UploadProgress } from './services/directUpload';
import './styles/index.css';

const { Title, Text } = Typography;

// Helper functions for formatting
function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

function formatSpeed(bytesPerSecond: number): string {
    if (bytesPerSecond === 0) return '0 B/s';
    const k = 1024;
    const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
    const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k));
    return Math.round((bytesPerSecond / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

function App() {
    const {
        appToken,
        tableId,
        fields,
        attachmentFields,
        loading,
        error,
        getAttachments,
        updateFieldValue,
        getAllRecords,
        getFieldValue
    } = useBaseData();

    const [currentRecordId, setCurrentRecordId] = useState<string>('');
    const [selectedFieldId, setSelectedFieldId] = useState<string>('');
    const [selectedRenameFieldId, setSelectedRenameFieldId] = useState<string>('');
    const [attachments, setAttachments] = useState<AttachmentInfo[]>([]);
    const [uploading, setUploading] = useState(false);

    // Progress tracking state
    const [progress, setProgress] = useState<any>(null);
    const [currentFileIndex, setCurrentFileIndex] = useState(0);
    const [totalFiles, setTotalFiles] = useState(0);

    // Parallel upload state
    const [fileProgresses, setFileProgresses] = useState<Map<string, any>>(new Map());
    const [activeUploads, setActiveUploads] = useState<Set<string>>(new Set());
    const [concurrencyLimit] = useState(8); // Upload 8 files at a time

    // Batch Upload State
    const [isBatchMode, setIsBatchMode] = useState(false);
    const [statusFieldId, setStatusFieldId] = useState<string>('');
    const [skipProcessed, setSkipProcessed] = useState(true);

    // Batch state placeholders
    const [batchProcessing, setBatchProcessing] = useState(false);
    const [batchProgress, setBatchProgress] = useState(0);
    const [batchTotal, setBatchTotal] = useState(0);
    const [batchLogs, setBatchLogs] = useState<string[]>([]);

    // Direct upload mode - DISABLED permanently due to CORS limitation
    // Browser cannot download directly from Feishu servers (no Access-Control-Allow-Origin header)
    // All file downloads must go through the Worker proxy
    const [useDirectUpload, setUseDirectUpload] = useState(false);
    const directUploadSupported = false; // CORS blocks direct browser downloads from Feishu

    // 监听当前选中的记录
    useEffect(() => {
        const unsubscribe = bitable.base.onSelectionChange(async (event) => {
            if (event.data.recordId) {
                setCurrentRecordId(event.data.recordId);
                if (!isBatchMode) {
                    setAttachments([]);
                    setProgress(null);
                }
            }
        });

        // 初始化时获取当前选中的记录
        bitable.base.getSelection().then((selection) => {
            if (selection?.recordId) {
                setCurrentRecordId(selection.recordId);
            }
        });

        return () => {
            unsubscribe();
        };
    }, [isBatchMode]);

    // 当选择了附件字段时，加载附件
    useEffect(() => {
        if (currentRecordId && selectedFieldId && !isBatchMode) {
            loadAttachments();
        }
    }, [currentRecordId, selectedFieldId, isBatchMode]);

    const loadAttachments = async () => {
        if (!currentRecordId || !selectedFieldId) return;
        const atts = await getAttachments(currentRecordId, selectedFieldId);
        setAttachments(atts);
    };

    // Promise-based polling helper
    const pollProgress = async (uploadId: string): Promise<boolean> => {
        let emptyCount = 0;
        const MAX_EMPTY_RETRIES = 15; // 15 seconds timeout for no data

        return new Promise((resolve) => {
            const check = async () => {
                try {
                    console.log(`[Poll] Checking ${uploadId}...`);
                    const data = await fetchProgress(uploadId);
                    console.log(`[Poll] Result:`, data);

                    if (data) {
                        emptyCount = 0; // Reset counter on successful data
                        setProgress(data);
                        if (data.status === 'completed') {
                            resolve(true);
                            return;
                        }
                        if (data.status === 'failed') {
                            resolve(false);
                            return;
                        }
                    } else {
                        emptyCount++;
                        console.warn(`[Poll] No data received (${emptyCount}/${MAX_EMPTY_RETRIES})`);

                        // If we have waited too long without any data
                        if (emptyCount >= MAX_EMPTY_RETRIES) {
                            console.error('[Poll] Timeout waiting for progress data');
                            Toast.warning('进度追踪超时，尝试继续处理下一个文件');
                            resolve(true);
                            return;
                        }
                    }
                } catch (e) {
                    console.error('Polling error:', e);
                }
                // Continue polling
                setTimeout(check, 1000);
            };
            check();
        });
    };

    // Parallel polling helper - tracks individual file progress
    const pollProgressParallel = async (uploadId: string, fileName: string): Promise<boolean> => {
        let emptyCount = 0;
        const MAX_EMPTY_RETRIES = 60; // 60 seconds timeout

        return new Promise((resolve) => {
            const check = async () => {
                try {
                    const data = await fetchProgress(uploadId);

                    if (data) {
                        emptyCount = 0;
                        // Update individual file progress in the Map
                        setFileProgresses(prev => {
                            const next = new Map(prev);
                            next.set(uploadId, { ...data, fileName });
                            return next;
                        });

                        if (data.status === 'completed') {
                            resolve(true);
                            return;
                        }
                        if (data.status === 'failed') {
                            resolve(false);
                            return;
                        }
                    } else {
                        emptyCount++;
                        if (emptyCount >= MAX_EMPTY_RETRIES) {
                            console.warn(`[Parallel] ${fileName} timeout`);
                            Toast.warning(`${fileName} 进度追踪超时`);
                            resolve(true);
                            return;
                        }
                    }
                } catch (e) {
                    console.error(`Polling error for ${fileName}:`, e);
                }
                setTimeout(check, 1000);
            };
            check();
        });
    };

    // Parallel upload helper - processes files in batches with retry
    const uploadFilesParallel = async (
        files: AttachmentInfo[],
        recordId: string,
        renameFieldName: string | undefined
    ): Promise<Array<{ file: string; success: boolean; error?: any }>> => {
        const results: Array<{ file: string; success: boolean; error?: any }> = [];
        const maxRetries = 3;
        const retryDelay = 2000; // 2 seconds

        // Helper function to upload a single file with retries
        const uploadWithRetry = async (att: AttachmentInfo): Promise<{ file: string; success: boolean; error?: any }> => {
            let lastError: any;

            for (let attempt = 1; attempt <= maxRetries; attempt++) {
                let uploadId: any;
                try {
                    // Start upload
                    uploadId = await uploadToGoogleDrive({
                        fileToken: att.fileToken,
                        fileName: att.name,
                        tableId,
                        recordId,
                        renameFieldId: selectedRenameFieldId || undefined,
                        renameFieldName: renameFieldName,
                        appToken
                    });

                    console.log(`[Parallel] Upload started for ${att.name} (attempt ${attempt}):`, uploadId);

                    // Safety check for object type
                    let finalUploadId = uploadId;
                    if (typeof uploadId === 'object') {
                        console.error('[Parallel] uploadId is an object! Attempting to fix...');
                        // @ts-ignore
                        finalUploadId = uploadId.uploadId || JSON.stringify(uploadId);
                    }

                    // Track this upload
                    setActiveUploads(prev => new Set(prev).add(finalUploadId));

                    // Poll with individual progress tracking
                    const success = await pollProgressParallel(finalUploadId, att.name);

                    // Remove from active uploads
                    setActiveUploads(prev => {
                        const next = new Set(prev);
                        next.delete(finalUploadId);
                        return next;
                    });

                    if (success) {
                        return { file: att.name, success: true };
                    } else {
                        throw new Error('Upload poll returned false');
                    }
                } catch (error: any) {
                    lastError = error;
                    console.warn(`[Retry] Attempt ${attempt}/${maxRetries} failed for ${att.name}:`, error.message);

                    // Clean up active uploads on error
                    if (uploadId) {
                        setActiveUploads(prev => {
                            const next = new Set(prev);
                            const uploadIdToRemove = typeof uploadId === 'object'
                                // @ts-ignore
                                ? (uploadId.uploadId || JSON.stringify(uploadId))
                                : uploadId;
                            next.delete(uploadIdToRemove);
                            return next;
                        });
                    }

                    // Wait before retry (exponential backoff)
                    if (attempt < maxRetries) {
                        const waitTime = retryDelay * attempt;
                        console.log(`[Retry] Waiting ${waitTime}ms before retry...`);
                        await new Promise(resolve => setTimeout(resolve, waitTime));
                    }
                }
            }

            // All retries failed
            console.error(`[Parallel] All ${maxRetries} attempts failed for ${att.name}:`, lastError);
            return { file: att.name, success: false, error: lastError };
        };

        // Process in batches of concurrencyLimit
        for (let i = 0; i < files.length; i += concurrencyLimit) {
            const batch = files.slice(i, i + concurrencyLimit);
            const batchPromises = batch.map(att => uploadWithRetry(att));
            const batchResults = await Promise.allSettled(batchPromises);
            results.push(...batchResults.map(r =>
                r.status === 'fulfilled' ? r.value : { file: 'unknown', success: false, error: r.reason }
            ));
        }

        return results;
    };

    // Direct upload helper - download from Feishu and upload to Drive directly in browser
    const uploadFilesDirectly = async (
        files: AttachmentInfo[],
        recordId: string,
        renameFieldName: string | undefined
    ): Promise<Array<{ file: string; success: boolean; error?: any }>> => {
        const results: Array<{ file: string; success: boolean; error?: any }> = [];

        // Process in batches of concurrencyLimit
        for (let i = 0; i < files.length; i += concurrencyLimit) {
            const batch = files.slice(i, i + concurrencyLimit);

            const batchPromises = batch.map(async (att) => {
                try {
                    setActiveUploads(prev => new Set(prev).add(att.fileToken));

                    // Get rename value if needed
                    const renameValue = renameFieldName
                        ? await getFieldValue(recordId, selectedRenameFieldId)
                        : undefined;

                    // Direct upload with progress
                    await directUploadFile(
                        att.fileToken,
                        att.name,
                        { appToken, tableId, recordId, fieldId: selectedFieldId },
                        renameValue?.toString(),
                        (progress: UploadProgress) => {
                            setFileProgresses(prev => {
                                const next = new Map(prev);
                                next.set(att.fileToken, {
                                    fileName: att.name,
                                    status: progress.phase,
                                    downloadProgress: progress.phase === 'downloading' ? progress.percent : 100,
                                    uploadProgress: progress.phase === 'uploading' ? progress.percent : 0,
                                    speed: progress.speed,
                                    stage: progress.phase === 'downloading' ? '从飞书下载中' : '上传到Drive中',
                                    processedBytes: progress.bytesTransferred,
                                    totalBytes: progress.totalBytes,
                                    startTime: Date.now(),
                                    lastUpdate: Date.now()
                                });
                                return next;
                            });
                        },
                        att.url  // Pass SDK-provided URL for direct download
                    );

                    return { file: att.name, success: true };
                } catch (error: any) {
                    console.error(`[Direct] Upload failed for ${att.name}:`, error);
                    return { file: att.name, success: false, error };
                } finally {
                    setActiveUploads(prev => {
                        const next = new Set(prev);
                        next.delete(att.fileToken);
                        return next;
                    });
                }
            });

            const batchResults = await Promise.allSettled(batchPromises);
            results.push(...batchResults.map(r =>
                r.status === 'fulfilled' ? r.value : { file: 'unknown', success: false, error: r.reason }
            ));
        }

        return results;
    };

    const handleUpload = async () => {
        if (attachments.length === 0 || !currentRecordId) {
            Toast.warning('当前记录没有附件');
            return;
        }
        if (!statusFieldId) {
            Toast.warning('请选择状态回写字段');
            return;
        }
        if (!appToken) {
            Toast.error('无法获取应用令牌');
            return;
        }

        setUploading(true);
        setTotalFiles(attachments.length);
        setCurrentFileIndex(0);
        setFileProgresses(new Map());
        setActiveUploads(new Set());

        try {
            // Find the name of the rename field
            const renameField = fields.find(f => f.id === selectedRenameFieldId);
            const renameFieldName = renameField ? renameField.name : undefined;

            // Use direct upload or Worker-based parallel upload
            const results = useDirectUpload
                ? await uploadFilesDirectly(attachments, currentRecordId, renameFieldName)
                : await uploadFilesParallel(attachments, currentRecordId, renameFieldName);

            const successCount = results.filter(r => r.success).length;
            const failCount = results.length - successCount;

            // Final status update
            const statusMsg = `上传完成: 成功 ${successCount}, 失败 ${failCount}`;
            if (failCount === 0) {
                Toast.success(statusMsg);
                await updateFieldValue(currentRecordId, statusFieldId, '上传成功');
            } else {
                Toast.warning(statusMsg);
                await updateFieldValue(currentRecordId, statusFieldId, `部分失败: 成功${successCount}/失败${failCount}`);
            }

        } catch (err: any) {
            console.error('Upload process exception:', err);
            Toast.error(`上传流程异常: ${err.message}`);
        } finally {
            setUploading(false);
            setProgress(null);
            setFileProgresses(new Map());
            setActiveUploads(new Set());
        }
    };

    const handleBatchUpload = async () => {
        if (!selectedFieldId || !statusFieldId) {
            Toast.warning('请先选择附件字段和状态回写字段');
            return;
        }
        if (!appToken || !tableId) {
            Toast.error('无法获取应用信息');
            return;
        }

        // Reset logs and state
        setBatchLogs([]);
        setBatchProcessing(true);
        setBatchProgress(0);
        setBatchTotal(0);

        try {
            // 1. Get all record IDs
            const recordIds = await getAllRecords();
            if (!recordIds || recordIds.length === 0) {
                Toast.warning('当前视图没有记录');
                setBatchProcessing(false);
                return;
            }

            // 2. Filter records (We have to do this iteratively or in chunks because we only have IDs)
            // To avoid blocking UI, we'll process and filter on the fly
            const recordsToProcessIds: string[] = [];

            setBatchLogs(prev => [`正在扫描 ${recordIds.length} 条记录...`, ...prev]);

            // Pre-scan loop to filter records
            for (const rid of recordIds) {
                if (skipProcessed) {
                    const status = await getFieldValue(rid, statusFieldId);
                    if (status && (String(status).includes('成功') || String(status).includes('Success'))) {
                        continue;
                    }
                }
                // Check attachments presence
                const atts = await getAttachments(rid, selectedFieldId);
                if (atts && atts.length > 0) {
                    recordsToProcessIds.push(rid);
                }
            }

            if (recordsToProcessIds.length === 0) {
                Toast.info('没有需要处理的记录');
                setBatchProcessing(false);
                return;
            }

            setBatchTotal(recordsToProcessIds.length);
            let processedCount = 0;

            // Helper function to process a single record
            const processRecord = async (recordId: string, index: number): Promise<{ recordId: string; success: number; fail: number }> => {
                const attachments = await getAttachments(recordId, selectedFieldId);

                setBatchLogs(prev => [`[${index + 1}/${recordsToProcessIds.length}] 处理记录: ${recordId} (${attachments.length} 个文件)`, ...prev]);

                const renameField = fields.find(f => f.id === selectedRenameFieldId);
                const renameFieldName = renameField ? renameField.name : undefined;

                const results = await uploadFilesParallel(attachments, recordId, renameFieldName);

                const recordSuccessCount = results.filter(r => r.success).length;
                const recordFailCount = results.length - recordSuccessCount;

                results.forEach(r => {
                    if (r.success) {
                        setBatchLogs(prev => [`✅ ${r.file}`, ...prev]);
                    } else {
                        setBatchLogs(prev => [`❌ ${r.file}`, ...prev]);
                    }
                });

                if (recordFailCount === 0) {
                    await updateFieldValue(recordId, statusFieldId, '上传成功');
                } else {
                    await updateFieldValue(recordId, statusFieldId, `部分失败: 成功${recordSuccessCount}/失败${recordFailCount}`);
                }

                processedCount++;
                setBatchProgress(processedCount);

                return { recordId, success: recordSuccessCount, fail: recordFailCount };
            };

            // Process 3 records at a time
            const recordBatchSize = 3;

            for (let i = 0; i < recordsToProcessIds.length; i += recordBatchSize) {
                const batch = recordsToProcessIds.slice(i, i + recordBatchSize);
                const promises = batch.map((recordId, batchIndex) =>
                    processRecord(recordId, i + batchIndex)
                );
                await Promise.allSettled(promises);
            }

            Toast.success(`批量处理完成! 共处理 ${processedCount} 条记录`);

        } catch (error: any) {
            console.error('Batch process error:', error);
            Toast.error(`批量处理异常: ${error.message}`);
            setBatchLogs(prev => [`⛔ 致命错误: ${error.message}`, ...prev]);
        } finally {
            setBatchProcessing(false);
            setProgress(null);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column' }}>
                <Spin size="large" />
                <Text style={{ marginTop: 16 }}>加载中...</Text>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: 20, textAlign: 'center' }}>
                <Text type="danger">错误: {error}</Text>
                <Button onClick={() => window.location.reload()} style={{ marginTop: 16 }}>重试</Button>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', background: 'var(--semi-color-bg-0)' }}>
            {/* Header */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--semi-color-border)', flexShrink: 0, background: 'var(--semi-color-bg-0)', zIndex: 10 }}>
                <Title heading={4} style={{ marginBottom: '12px', textAlign: 'center' }}>Feishu Drive Sync</Title>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <div style={{ background: 'var(--semi-color-fill-0)', padding: '4px', borderRadius: '8px', display: 'flex', width: '100%' }}>
                        <Button
                            theme={!isBatchMode ? 'solid' : 'light'}
                            type={!isBatchMode ? 'primary' : 'tertiary'}
                            onClick={() => setIsBatchMode(false)}
                            style={{ borderRadius: '6px', flex: 1 }}
                        >
                            单记录
                        </Button>
                        <Button
                            theme={isBatchMode ? 'solid' : 'light'}
                            type={isBatchMode ? 'primary' : 'tertiary'}
                            onClick={() => setIsBatchMode(true)}
                            style={{ borderRadius: '6px', flex: 1 }}
                        >
                            批量
                        </Button>
                    </div>
                </div>
            </div>

            {/* Scrollable Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

                    {/* Basic Settings Section */}
                    <div style={{ width: '100%' }}>
                        <Text strong style={{ marginBottom: '12px', display: 'block', fontSize: '14px' }}>基础设置</Text>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div style={{ width: '100%' }}>
                                <Text type="secondary" size="small" style={{ marginBottom: '4px', display: 'block' }}>附件字段 (必选)</Text>
                                <Select
                                    style={{ width: '100%' }}
                                    placeholder="选择附件列"
                                    value={selectedFieldId}
                                    onChange={(value) => setSelectedFieldId(value as string)}
                                    disabled={batchProcessing || uploading}
                                >
                                    {attachmentFields.map(field => (
                                        <Select.Option key={field.id} value={field.id}>{field.name}</Select.Option>
                                    ))}
                                </Select>
                            </div>

                            <div style={{ width: '100%' }}>
                                <Text type="secondary" size="small" style={{ marginBottom: '4px', display: 'block' }}>重命名依据 (可选)</Text>
                                <Select
                                    style={{ width: '100%' }}
                                    placeholder="不重命名"
                                    value={selectedRenameFieldId}
                                    onChange={(value) => setSelectedRenameFieldId(value as string)}
                                    disabled={uploading || batchProcessing}
                                    showClear
                                >
                                    {fields.map(field => (
                                        <Select.Option key={field.id} value={field.id}>{field.name}</Select.Option>
                                    ))}
                                </Select>
                            </div>

                            <div style={{ width: '100%' }}>
                                <Text type="secondary" size="small" style={{ marginBottom: '4px', display: 'block' }}>状态回写 (必选)</Text>
                                <Select
                                    style={{ width: '100%' }}
                                    placeholder="选择文本列"
                                    value={statusFieldId}
                                    onChange={(value) => setStatusFieldId(value as string)}
                                    disabled={uploading || batchProcessing}
                                >
                                    {fields
                                        .filter(f => f.type === 1 || f.type === 15)
                                        .map(field => (
                                            <Select.Option key={field.id} value={field.id}>{field.name}</Select.Option>
                                        ))}
                                </Select>
                            </div>
                        </div>
                    </div>

                    <Divider />

                    {/* Mode Specific Options */}
                    {isBatchMode ? (
                        <div style={{ width: '100%' }}>
                            <Text strong style={{ marginBottom: '12px', display: 'block', fontSize: '14px' }}>批量选项</Text>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Text>跳过已上传</Text>
                                    <Switch checked={skipProcessed} onChange={setSkipProcessed} disabled={batchProcessing} />
                                </div>
                                <Banner
                                    type="info"
                                    description="将处理当前视图所有记录。请先筛选。"
                                    closeIcon={null}
                                    style={{ borderRadius: '4px' }}
                                />

                                {batchProcessing && (
                                    <div style={{ marginTop: '8px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                            <Text type="secondary" size="small">进度: {batchProgress}/{batchTotal}</Text>
                                        </div>
                                        <Progress
                                            percent={batchTotal > 0 ? Math.round((batchProgress / batchTotal) * 100) : 0}
                                            stroke="var(--semi-color-primary)"
                                            style={{ height: '6px' }}
                                        />
                                    </div>
                                )}

                                {batchProcessing && (
                                    <div style={{
                                        marginTop: '12px',
                                        padding: '16px',
                                        background: 'var(--semi-color-fill-0)',
                                        borderRadius: '8px',
                                        border: '1px solid var(--semi-color-border)'
                                    }}>
                                        <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Text strong>当前文件处理中...</Text>
                                            <Spin size="small" />
                                        </div>

                                        {activeUploads.size > 0 || fileProgresses.size > 0 ? (
                                            <>
                                                <Text style={{ display: 'block', marginBottom: '8px', fontSize: '13px' }}>
                                                    并行上传 {activeUploads.size} 个文件
                                                </Text>

                                                {Array.from(fileProgresses.entries()).map(([uploadId, prog]) => (
                                                    <div key={uploadId} style={{ marginBottom: '12px' }}>
                                                        <Text size="small" style={{ display: 'block', marginBottom: '4px' }}>
                                                            {prog.fileName} - {prog.stage}
                                                        </Text>
                                                        <Progress
                                                            percent={prog.uploadProgress || prog.downloadProgress || 0}
                                                            size="small"
                                                            stroke={prog.status === 'uploading' ? 'var(--semi-color-success)' : 'var(--semi-color-primary)'}
                                                        />
                                                    </div>
                                                ))}
                                            </>
                                        ) : (
                                            <div style={{ textAlign: 'center', padding: '12px' }}>
                                                <Text type="secondary">准备下一批文件...</Text>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {batchLogs.length > 0 && (
                                    <div style={{
                                        background: 'var(--semi-color-fill-0)',
                                        padding: '12px',
                                        borderRadius: '8px',
                                        border: '1px solid var(--semi-color-border)',
                                        maxHeight: '200px',
                                        overflowY: 'auto',
                                        marginTop: '12px'
                                    }}>
                                        <Text strong style={{ marginBottom: '8px', display: 'block' }}>批量日志</Text>
                                        {batchLogs.map((log, idx) => (
                                            <Text key={idx} size="small" style={{ display: 'block', marginBottom: '4px', fontFamily: 'monospace' }}>
                                                {log}
                                            </Text>
                                        ))}
                                    </div>
                                )}

                                <Button
                                    onClick={handleBatchUpload}
                                    loading={batchProcessing}
                                    block
                                    theme="solid"
                                    type="primary"
                                    icon={<IconUpload />}
                                >
                                    {batchProcessing ? '处理中...' : '开始批量上传'}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div style={{ width: '100%' }}>
                            <Text strong style={{ marginBottom: '12px', display: 'block', fontSize: '14px' }}>单记录选项</Text>
                            {!currentRecordId ? (
                                <Banner
                                    type="warning"
                                    description="请在表格中选择一条记录"
                                    closeIcon={null}
                                    style={{ marginBottom: '16px', borderRadius: '4px' }}
                                />
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    {attachments.length > 0 && (
                                        <div style={{
                                            background: 'var(--semi-color-fill-0)',
                                            padding: '12px',
                                            borderRadius: '8px',
                                            border: '1px solid var(--semi-color-border)'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                                <IconFile size="large" style={{ marginRight: '8px' }} />
                                                <Text strong>{attachments.length} 个附件</Text>
                                            </div>
                                            {attachments.map((att, idx) => (
                                                <Text key={idx} size="small" style={{ display: 'block', marginBottom: '4px' }}>
                                                    {idx + 1}. {att.name}
                                                </Text>
                                            ))}
                                        </div>
                                    )}

                                    {/* Multi-file parallel progress display */}
                                    {uploading && activeUploads.size > 0 && (
                                        <div style={{ marginTop: '12px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                <Text strong>正在并行上传 {activeUploads.size} 个文件</Text>
                                                <Text type="secondary" size="small">同时 {concurrencyLimit} 个</Text>
                                            </div>

                                            {Array.from(fileProgresses.entries()).map(([uploadId, prog]) => (
                                                <div key={uploadId} style={{
                                                    marginTop: '8px',
                                                    padding: '12px',
                                                    background: 'var(--semi-color-fill-0)',
                                                    borderRadius: '6px',
                                                    border: '1px solid var(--semi-color-border)'
                                                }}>
                                                    <Text size="small" style={{ display: 'block', marginBottom: '4px' }}>{prog.fileName}</Text>
                                                    <Progress
                                                        percent={prog.uploadProgress || prog.downloadProgress || 0}
                                                        size="small"
                                                        style={{ marginTop: '4px' }}
                                                        stroke={prog.status === 'uploading' ? 'var(--semi-color-success)' : 'var(--semi-color-primary)'}
                                                    />
                                                    <Text type="tertiary" size="small" style={{ display: 'block', marginTop: '4px' }}>
                                                        {prog.stage}
                                                    </Text>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <Button
                                        onClick={handleUpload}
                                        loading={uploading}
                                        disabled={attachments.length === 0}
                                        block
                                        theme="solid"
                                        type="primary"
                                        icon={<IconUpload />}
                                    >
                                        {uploading ? `上传中 (${activeUploads.size} 个进行中)...` : `上传所有文件 (${attachments.length})`}
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default App;
